import { useMemo } from "react";
import { Bot, Colors } from "../../../_app/types";
import { Stack } from "@mui/material";
import formatNumber from "../../../utils/formatNumber";
import { calcApr, calcRoi, formatCurrency, printFloatValue } from "../../../utils/utils";
import DottedTextField from "../../../_components/DottedTextField";
import CustomPaper from "../../../_components/CustomPaper";
import BadgeDirection from "../../../_components/BadgeDirection";
import AdronTypography from "../../../_components/AdronTypography";
import Text from "../../../_components/Text";

interface Props {
  bot: Bot;
}

export function FutureBotPerformanceInfo({ bot }: Props) {
  const APR = useMemo(() => {
    return calcApr(bot.realizedProfit + bot.unrealizedPnl, bot.margin, bot.runtime) * 100;
  }, [bot.realizedProfit, bot.unrealizedPnl, bot.margin, bot.runtime]);

  const ROI = useMemo(() => {
    return (
      calcRoi(bot.realizedProfit + bot.unrealizedPnl, bot.initialAmountQuote, bot.leverage) * 100
    );
  }, [bot.realizedProfit, bot.unrealizedPnl, bot.initialAmountQuote]);

  return (
    <Stack width="100%">
      <Text variant="h2" mb="16px">
        Performance
      </Text>
      <Stack width="100%" direction="column" gap="8px">
        <DottedTextField placeholder={"Grid completed"} value={bot.gridsCompleted} />
        <DottedTextField
          placeholder={"Grid profit"}
          value={`${formatCurrency(formatNumber(bot.allGridsProfit), bot.quote)}`}
        />
        <DottedTextField
          placeholder={"Total fees"}
          value={`${formatCurrency(formatNumber(bot.totalFees), bot.quote)}`}
        />
        <DottedTextField
          placeholder={"Realized profit "}
          value={`${formatCurrency(formatNumber(bot.realizedProfit), bot.quote)}`}
        />

        {bot.status !== "SHUTDOWN" && (
          <CustomPaper fullWidth>
            <Stack direction={"column"} spacing={1}>
              <Stack direction={"row"} alignItems={"center"} justifyContent={"space-between"}>
                <DottedTextField placeholder={"Current position"} value={""}>
                  {bot.currentPositionBase === 0 && <></>}
                  {bot.currentPositionBase > 0 && <BadgeDirection gridDirection={"Long"} />}
                  {bot.currentPositionBase < 0 && <BadgeDirection gridDirection={"Short"} />}
                </DottedTextField>
                {/* TODO: Поправить компоненты текста */}
                <AdronTypography
                  style={{ width: "fit-content", whiteSpace: "nowrap", marginLeft: "3px" }}
                  value={`${
                    bot.currentPositionBase < 0
                      ? printFloatValue(bot.currentPositionBase * -1)
                      : printFloatValue(bot.currentPositionBase)
                  } ${bot.base}`}
                />
              </Stack>
              <DottedTextField
                placeholder={"Entry price"}
                value={`${formatCurrency(formatNumber(bot.avgPositionOpeningPrice), bot.quote)}`}
              />
              <DottedTextField
                valueColor={
                  bot.unrealizedPnl > 0
                    ? Colors["Success"]
                    : bot.unrealizedPnl < 0
                      ? Colors["Destructive"]
                      : undefined
                }
                placeholder={"Unrealized PnL"}
                value={`${formatCurrency(formatNumber(bot.unrealizedPnl), bot.quote)}`}
              />
            </Stack>
          </CustomPaper>
        )}

        <DottedTextField
          placeholder={"Total PnL"}
          value={`${formatCurrency(formatNumber(bot.realizedProfit + bot.unrealizedPnl), bot.quote)}`}
        />
        <DottedTextField
          placeholder={"ROI"}
          value={`
					${formatNumber(ROI)}%`}
        />
        <DottedTextField placeholder={"APR"} value={`${formatNumber(APR)}%`} />
      </Stack>
    </Stack>
  );
}
